<template>
  <div class="accordion-section">
    <div class="accordion-container">
      <div 
        v-for="(item, index) in items" 
        :key="index"
        class="accordion-item"
        :class="{ 'is-active': activeIndex === index }"
        @mouseenter="activeIndex = index"
      >
        <div class="glass-overlay"></div>
        <div class="item-content">
          <h3 class="item-title">{{ t(item.title) }}</h3>
          <div class="item-description">
            <p :title="t(item.content)" class="content-text">{{ t(item.content) }}</p>
            <div class="button-container">
              <el-button type="primary" class="learn-more" @click="navigateTo(item.link)">{{ t('home.accordion.learnMore') }}</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()
const activeIndex = ref(0)

// 定义props，允许从外部传入items
const props = defineProps({
  items: {
    type: Array,
    default: () => []
  }
})

const navigateTo = (link: string) => {
  if (link.startsWith('/')) {
    router.push(link)
  } else {
    window.location.href = link
  }
}

// 默认的accordionItems，当外部没有传入items时使用
const defaultAccordionItems = [
  {
    title: 'home.accordion.items.company.title',
    content: 'home.accordion.items.company.content',
    image: '/images/AccordionSection.jpg',
    link: '/company'
  },
  {
    title: 'home.accordion.items.products.title',
    content: 'home.accordion.items.products.content',
    image: '/images/AccordionSection.jpg',
    link: 'products'
  },
  {
    title: 'home.accordion.items.applications.title',
    content: 'home.accordion.items.applications.content',
    image: '/images/AccordionSection.jpg',
    link: 'applications'
  },
  {
    title: 'home.accordion.items.news.title',
    content: 'home.accordion.items.news.content',
    image: '/images/AccordionSection.jpg',
    link: 'news'
  },
  {
    title: 'home.accordion.items.contact.title',
    content: 'home.accordion.items.contact.content',
    image: '/images/AccordionSection.jpg',
    link: 'contact'
  }
]
</script>

<style scoped lang="scss">
.accordion-section {
  padding: 0;
  background: transparent;
  position: absolute;
  bottom: 20px;
  left: 0;
  width: 100%;
  z-index: 10;
}

.accordion-container {
  display: flex;
  max-width: 1400px;
  height: 220px;
  margin: 0 auto;
  gap: 8px;
  padding: 0 0 0 50px;
}

 // Start of Selection
.accordion-item {
  flex: 1;
  position: relative;
  transition: all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  cursor: pointer;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
 
  
  .item-title {
    height: auto; /* 允许标题自适应高度 */
    overflow: hidden; /* 隐藏溢出部分 */
    text-overflow: ellipsis; /* 使用省略号表示溢出 */
  }

  &.is-active {
    flex: 3;

    .glass-overlay {
      background-color: rgba(189, 215, 238, 0.6);
      backdrop-filter: blur(12px) saturate(180%);
    }

    .item-content {
      opacity: 1;
      transform: translateX(0);
    }

    .item-description {
      opacity: 1;
      transform: translateY(0);
      transition-delay: 0.3s;
    }
  }
}

.glass-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(189, 215, 238, 0.6);
  backdrop-filter: blur(8px) saturate(150%);
  transition: all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  z-index: 1;
  border: 1px solid rgba(189, 215, 238, 0.6);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.05);
}

.item-content {
  position: relative;
  z-index: 2;
  color: #333;
  padding: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  opacity: 0.8;
  transform: translateX(-15px);
  transition: all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
}

.item-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 16px;
  white-space: nowrap;
  color: #222;
  letter-spacing: 0.5px;
}

.item-description {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s cubic-bezier(0.25, 0.1, 0.25, 1);
  height: 110px; /* 固定高度 */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  .content-text {
    font-size: 15px;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: #333;
    position: relative;
    
    &:hover::after {
      content: attr(title);
      position: absolute;
      bottom: 100%;
      left: 0;
      width: auto;
      min-width: 200px;
      max-width: 300px;
      padding: 10px;
      background-color: rgba(0, 0, 0, 0.8);
      color: #fff;
      border-radius: 6px;
      font-size: 14px;
      line-height: 1.5;
      z-index: 100;
      white-space: normal;
      box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(5px);
      transition: all 0.3s ease;
      pointer-events: none;
    }
  }
  
  .button-container {
    margin-top: auto;
  }
}

.learn-more {
  margin-top: 14px;
  font-weight: 500;
}

@media screen and (max-width: 1200px) {
  .accordion-container {
    max-width: 100%;
    padding: 0 15px;
  }
}

@media screen and (max-width: 992px) {
  .accordion-container {
    height: 220px;
  }
  
  .item-content {
    padding: 25px;
  }
  
  .item-title {
    font-size: 20px;
    margin-bottom: 12px;
  }
  
  .item-description {
    height: 75px;
  }
}

@media screen and (max-width: 768px) {
  .accordion-section {
    bottom: 15px;
  }

  .accordion-container {
    height: 180px;
    gap: 4px;
    padding: 0 10px;
  }

  .item-content {
    padding: 18px;
  }

  .item-title {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .item-description {
    height: 65px;
    
    .content-text {
      font-size: 14px;
      line-height: 1.5;
      -webkit-line-clamp: 2;
    }
  }
  
  .learn-more {
    margin-top: 8px;
    font-size: 12px;
    padding: 6px 12px;
  }
}

@media screen and (max-width: 576px) {
  .accordion-section {
    bottom: 10px;
  }
  
  .accordion-container {
    height: 160px;
    gap: 3px;
  }
  
  .accordion-item {
    border-radius: 6px;
    
    &.is-active {
      flex: 2;
    }
  }

  .item-content {
    padding: 12px;
  }

  .item-title {
    font-size: 16px;
    margin-bottom: 5px;
  }
  
  .glass-overlay {
    backdrop-filter: blur(6px) saturate(140%);
  }
  
  .item-description {
    height: 55px;
    
    .content-text {
      font-size: 12px;
      line-height: 1.4;
      -webkit-line-clamp: 2;
    }
    
    .content-text:hover::after {
      min-width: 150px;
      max-width: 200px;
      font-size: 12px;
      padding: 8px;
    }
  }
  
  .learn-more {
    margin-top: 5px;
    font-size: 12px;
    padding: 4px 10px;
    height: auto;
    line-height: 1.5;
  }
}
</style> 